import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';

interface CheckoutButtonProps {
  reportPrice: number;
  subscriptionPrice: number | null;
  subscriptionPlan: 'starter' | 'pro' | 'premium' | null;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  user: any;
}

export function CheckoutButton({
  reportPrice,
  subscriptionPrice,
  subscriptionPlan,
  isProcessing,
  setIsProcessing,
  user,
}: CheckoutButtonProps) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = async () => {
    if (!user) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Please log in to continue');
      }

      const origin = window.location.origin;
      const successUrl = `${origin}/thank-you?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}/pricing`;

      let functionName: string;
      let requestBody: any = {
        successUrl,
        cancelUrl,
      };

      if (subscriptionPrice && subscriptionPlan) {
        functionName = 'stripe-create-hybrid-checkout';
        requestBody.tier = subscriptionPlan;
      } else {
        functionName = 'stripe-create-report-checkout';
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  const buttonText = () => {
    if (isProcessing) return 'Processing...';
    if (!user) return 'Log in to purchase';
    if (subscriptionPrice) {
      return `Purchase for $${reportPrice + subscriptionPrice}`;
    }
    return `Purchase Report for $${reportPrice}`;
  };

  return (
    <div>
      <button
        onClick={handleCheckout}
        disabled={isProcessing}
        className={`w-full py-4 px-6 rounded-lg font-bold text-white transition-all flex items-center justify-center ${
          isProcessing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 shadow-lg hover:shadow-xl'
        }`}
      >
        {isProcessing ? (
          <>
            <Loader2 className="animate-spin mr-2" size={20} />
            {buttonText()}
          </>
        ) : (
          <>
            <Lock className="mr-2" size={20} />
            {buttonText()}
          </>
        )}
      </button>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
}
